/**
 * ConfigManager - Fetches and caches consent banner configuration from API
 *
 * 3-tier fallback:
 * 1. Fresh API fetch
 * 2. localStorage cache (15-min TTL)
 * 3. Safe defaults
 */

import type {
  ConsentPopInitConfig,
  WidgetConfigResponse,
  BannerConfig,
  GeoInfo,
} from "./types";
import { allowsConfiguredEdge } from "./edge-routing";

declare const __CONSENTPOP_API_URL__: string;
declare const __CONSENTPOP_CONFIG_API_URL__: string;
declare const __CONSENTPOP_DEBUG__: boolean;

const CACHE_DURATION = 1000 * 60 * 15; // 15 minutes
const MAX_CACHE_AGE = 1000 * 60 * 60 * 24; // one-day outage ceiling
const API_TIMEOUT = 10000; // 10 seconds

const DEFAULT_CONFIG: BannerConfig = {
  mode: "opt-in",
  autoShow: true,
  disablePageInteraction: false,
  bannerDensity: "standard",
  hideAfterAcceptAll: false,
  preferencesLauncher: "hidden",
  preferencesLauncherStyle: "labeled",
  preferencesLauncherPosition: "bottom-right",
  autoHideMode: "none",
  autoHideDelaySeconds: 15,
  autoHideScrollPercent: 50,
  layout: "bar",
  layoutVariant: "inline",
  position: "bottom",
  alignment: "center",
  flipButtons: false,
  equalWeightButtons: true,
  themePreset: "simple",
  primaryColor: "#235ac8",
  backgroundColor: "#ffffff",
  textColor: "#172b4a",
  buttonStyle: "rounded",
  darkMode: false,
  customCss: null,
  categories: {
    necessary: { enabled: true, readOnly: true },
    analytics: { enabled: false },
    marketing: { enabled: false },
    functionality: { enabled: false },
  },
  googleConsentMode: false,
  cookieName: "cp_consent",
  cookieExpiry: 365,
  defaultLanguage: "en",
  autoDetectLanguage: true,
  translations: {},
  revision: 0,
  configVersion: 0,
};

const DEFAULT_GEO: GeoInfo = {
  country: null,
  region: null,
  isGdpr: false,
  isCcpa: false,
  resolvedMode: "opt-in",
};

export type ConfigLoadSource = "network" | "cache" | "stale-cache";

export type ConfigLoadResult = {
  config: BannerConfig;
  geo: GeoInfo;
  source: ConfigLoadSource;
};

export class ConfigManager {
  private initConfig: ConsentPopInitConfig;
  private cacheKey: string;

  constructor(config: ConsentPopInitConfig) {
    this.initConfig = config;
    this.cacheKey = `cp_config_${config.apiKey}`;
  }

  /**
   * Load banner config + geo info.
   * Returns fresh from API, falls back to cache, then defaults.
   */
  async load(): Promise<ConfigLoadResult> {
    try {
      const response = await this.fetchFromAPI();
      const result = { config: response.config, geo: response.geo };
      this.saveToCache(result);
      return { ...result, source: "network" };
    } catch (error: unknown) {
      // Re-throw subscription errors — widget should not render
      if (
        error instanceof Error &&
        (error as Error & { code?: string }).code === "SUBSCRIPTION_REQUIRED"
      ) {
        throw error;
      }
      // Re-throw rate limit errors
      if (
        error instanceof Error &&
        (error as Error & { code?: string }).code === "RATE_LIMITED"
      ) {
        throw error;
      }
      if (
        error instanceof Error &&
        (["DOMAIN_NOT_ALLOWED", "SITE_PAUSED", "INVALID_API_KEY"] as const).includes(
          (error as Error & { code?: string }).code as
            | "DOMAIN_NOT_ALLOWED"
            | "SITE_PAUSED"
            | "INVALID_API_KEY",
        )
      ) {
        throw error;
      }

      // Use only a bounded last-known configuration. Unknown current location
      // cannot justify a permissive regional mode.
      const cached = this.loadFromCache(true);
      if (cached) {
        if (__CONSENTPOP_DEBUG__)
          console.debug("[ConsentPop] API failed, using cached config");
        return {
          ...cached,
          source: this.isCacheFresh(cached.timestamp) ? "cache" : "stale-cache",
        };
      }

      // Do not render a plausible-but-wrong banner for a different tenant.
      // The caller records the failure and leaves the page untouched.
      const unavailable = new Error("Consent configuration is unavailable") as Error & {
        code: string;
      };
      unavailable.code = "CONFIG_UNAVAILABLE";
      throw unavailable;
    }
  }

  private async fetchFromAPI(): Promise<WidgetConfigResponse> {
    const originUrl = this.getAPIUrl();
    const edgeUrl = allowsConfiguredEdge(this.initConfig.apiUrl) && typeof __CONSENTPOP_CONFIG_API_URL__ !== "undefined"
      ? __CONSENTPOP_CONFIG_API_URL__ : "";
    if (!edgeUrl || edgeUrl === originUrl || originUrl.startsWith("http://localhost") || originUrl.startsWith("http://127.0.0.1")) {
      return this.fetchFromUrl(originUrl);
    }
    try {
      return await this.fetchFromUrl(edgeUrl);
    } catch (error) {
      const status = error && typeof error === "object" && "status" in error
        ? (error as { status?: unknown }).status : undefined;
      // Do not bypass a domain, subscription, pause, or rate-limit denial.
      const code = error && typeof error === "object" && "code" in error
        ? (error as { code?: unknown }).code : undefined;
      if (typeof status === "number" && status < 500 && !(status === 404 && code !== "INVALID_API_KEY")) throw error;
      return this.fetchFromUrl(originUrl);
    }
  }

  private async fetchFromUrl(apiUrl: string): Promise<WidgetConfigResponse> {
    const url = `${apiUrl}/api/widget/${this.initConfig.apiKey}?_t=${Date.now()}`;
    const currentDomain =
      typeof window !== "undefined" ? window.location.hostname : "unknown";

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Widget-Domain": currentDomain,
          "Cache-Control": "no-cache, no-store, must-revalidate",
        },
        cache: "no-store",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 404 && errorData.code === "INVALID_API_KEY") {
          const err = new Error("Invalid API key") as Error & { code: string; status: number };
          err.code = "INVALID_API_KEY";
          err.status = response.status;
          throw err;
        }

        if (
          response.status === 403 &&
          errorData.code === "SUBSCRIPTION_REQUIRED"
        ) {
          const err = new Error("Subscription inactive") as Error & {
            code: string;
            status: number;
          };
          err.code = "SUBSCRIPTION_REQUIRED";
          err.status = response.status;
          throw err;
        }

        if (
          response.status === 403 &&
          (errorData.code === "DOMAIN_NOT_ALLOWED" ||
            errorData.code === "SITE_PAUSED")
        ) {
          const err = new Error("Widget is not allowed on this site") as Error & {
            code: string;
            status: number;
          };
          err.code = errorData.code;
          err.status = response.status;
          throw err;
        }

        if (response.status === 429) {
          const err = new Error("Rate limited") as Error & { code: string; status: number };
          err.code = "RATE_LIMITED";
          err.status = response.status;
          throw err;
        }

        const err = new Error(`API returned ${response.status}`) as Error & { status: number };
        err.status = response.status;
        throw err;
      }

      const data: WidgetConfigResponse = await response.json();

      if (!data.config) {
        throw new Error("Invalid response - missing config");
      }

      // Merge with defaults to ensure all fields present
      return {
        ...data,
        config: { ...DEFAULT_CONFIG, ...data.config },
        geo: { ...DEFAULT_GEO, ...data.geo },
      };
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("API request timed out after 10 seconds");
      }
      throw error;
    }
  }

  private getAPIUrl(): string {
    if (this.initConfig.apiUrl) return this.initConfig.apiUrl;

    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host === "localhost" || host === "127.0.0.1") {
        return "http://localhost:3000";
      }
    }

    if (typeof __CONSENTPOP_API_URL__ !== "undefined") {
      return __CONSENTPOP_API_URL__;
    }

    return "https://consentpop.com";
  }

  private saveToCache(data: { config: BannerConfig; geo: GeoInfo }): void {
    try {
      if (typeof localStorage === "undefined") return;
      localStorage.setItem(
        this.cacheKey,
        JSON.stringify({
          data,
          timestamp: Date.now(),
        }),
      );
    } catch {
      /* localStorage full or disabled — ignore */
    }
  }

  private isCacheFresh(timestamp: number): boolean {
    return Date.now() - timestamp <= CACHE_DURATION;
  }

  private loadFromCache(
    allowStale = false,
  ): { config: BannerConfig; geo: GeoInfo; timestamp: number } | null {
    try {
      if (typeof localStorage === "undefined") return null;
      const raw = localStorage.getItem(this.cacheKey);
      if (!raw) return null;

      const { data, timestamp } = JSON.parse(raw);
      if (typeof timestamp !== "number" || !Number.isFinite(timestamp) ||
          timestamp > Date.now() || Date.now() - timestamp > MAX_CACHE_AGE) return null;
      if (!allowStale && !this.isCacheFresh(timestamp)) return null;

      const config = { ...DEFAULT_CONFIG, ...data.config };
      const geo = { ...DEFAULT_GEO, ...data.geo };

      // Geolocation cannot be verified during an outage, even for a fresh
      // cache. Require opt-in for optional services until policy is refreshed.
      geo.resolvedMode = "opt-in";

      return {
        config,
        geo,
        timestamp,
      };
    } catch {
      return null;
    }
  }
}
